import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import _ from "lodash";

import { AppDynamicForm, FormPage, TabPanel, ToastType, notifyToast, FormButtonToolbar } from "../../components-old/ComponentsIndex";
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import { selectEmployee, selectSessionConfig } from "../../store/store-selector";
import {
  AuthSignConfigFormData,
  AuthSignConfigFormErrorData,
  setAuthSignConfigFormFieldContent,
} from "./CreateAuthSignConfigFormData";
import { handlePostAction } from "../../utils/event-api-invoke";
import { isFormSubmitDisable, validateInput } from "./AuthSignValidation";

const CreateAuthSignConfigForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedAuthSignConfigId = searchParams.get("authsignsonfigid");

  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);
  const loggedInEmployee = useSelector(selectEmployee);

  const navigationData = location.state;

  /**************************************************
  //Form state variables
  **************************************************/

  const [formStateData, setFormStateData] = useState({
    formData: _.cloneDeep(AuthSignConfigFormData),
    formError: _.cloneDeep(AuthSignConfigFormErrorData),
    branchList: [],
    disableFormSubmit: true,
    validated: false,
  });

  /**************************************************
  //Fetch APIs
  **************************************************/

  const formAPIRequestData = () => {
    try {
      let _data = {
        HashId: selectedAuthSignConfigId ? selectedAuthSignConfigId : "",
      };
      Object.entries(formStateData.formData).forEach(([key, objProps]) => {
        _data[key] = objProps.value;
        _data.ModifiedBy = loggedInEmployee.employeeWorkEmail;

        if (navigationData) {
          _data.CreatedBy = navigationData.CreatedBy;
        } else {
          _data.CreatedBy = loggedInEmployee.employeeWorkEmail;
        }
      });

      return [
        {
          data: _data,
        },
      ];
    } catch (e) {
      alert(e);
    }
  };

  const saveAuthSignConfig = async () => {
    try {
      let _apiData = formAPIRequestData();

      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        authorisedsignatureuserconfig: [],
      };
      _apiData.forEach((item) => {
        _postRequest.authorisedsignatureuserconfig.push({
          ...item.data,
          Deleted: false,
          CountryCode: settingsConfig.countryCode,
          BusinessCode: settingsConfig.businessCode,
        });
      });

      spinnerCtx.setLoadingIndicator(true);
      let res = await handlePostAction(_postRequest, "authsignconfig");

      if (res?.status === 200) {
        if (res?.data?.response && res.data.response.errorNumber > 0) {
          selectedAuthSignConfigId
            ? notifyToast("Authorised signature Config update failed", ToastType.ERROR)
            : notifyToast("Authorised signature Config create failed", ToastType.ERROR);
          spinnerCtx.setLoadingIndicator(false);
        } else {
          selectedAuthSignConfigId
            ? notifyToast("Authorised signature config updated successfully", ToastType.SUCCESS)
            : notifyToast("Authorised signature config created successfully", ToastType.SUCCESS);
          handleCancel();
        }
      } else {
        notifyToast("Authorised signature Config request failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Event definitions
  **************************************************/
  const handleOnSave = async (event) => {
    try {
      const isFormValid = Object.values(formStateData.formError).every((x) => x === null || x === "");
      if (!formStateData.disableFormSubmit && isFormValid) {
        event.preventDefault();
        event.stopPropagation();

        await saveAuthSignConfig();
      }
      setFormStateData({ ...formStateData });
    } catch (err) {
      alert(err);
    }
  };

  const handleCancel = (event) => {
    try {
      setFormStateData({
        formData: _.cloneDeep(AuthSignConfigFormData),
        formError: _.cloneDeep(AuthSignConfigFormErrorData),
        branchList: [],
        disableFormSubmit: true,
        validated: false,
      });
      navigate("/authsignconfiglist");
    } catch (err) {
      alert(err);
    }
  };

  const setFieldData = (field, val) => {
    try {
      let _fieldData = { ...formStateData.formData };
      let _errorData = { ...formStateData.formError };
      _fieldData[field].value = val;

      if (_errorData[field]) {
        _errorData[field] = null;
        setFormStateData((prevState) => ({
          ...prevState,
          formData: _fieldData,
          formError: _errorData,
          disableFormSubmit: isFormSubmitDisable(_fieldData, _errorData),
        }));
      } else {
        setFormStateData((prevState) => ({
          ...prevState,
          formData: _fieldData,
          disableFormSubmit: isFormSubmitDisable(_fieldData, _errorData),
        }));
      }
    } catch (e) {
      alert(e);
    }
  };

  const onFieldValueChange = (e) => {
    try {
      setFieldData(e.target.id, e.target.value);
    } catch (err) {
      alert(err);
    }
  };

  const onBlurHandler = async (e) => {
    try {
      await validateInput(
        e,
        formStateData,
        setFormStateData,
        spinnerCtx,
        settingsConfig.businessCode,
        settingsConfig.countryCode
      );
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form Data & State handler
  **************************************************/
  const FormFieldContent = setAuthSignConfigFormFieldContent(
    t,
    formStateData.formData,
    formStateData.formError,
    selectedAuthSignConfigId,
    onFieldValueChange,
    onBlurHandler
  );

  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {
    const initFormComponents = () => {
      if (selectedAuthSignConfigId) {
        let _formData = { ...formStateData.formData };
        Object.entries(_formData).forEach(([objKey, objProps]) => {
          Object.entries(navigationData).forEach(([key, props]) => {
            if (objKey === key) {
              _formData[objKey].value = props;
            }
          });
        });
        setFormStateData((prevData) => ({
          ...prevData,
          formData: _formData,
          disableFormSubmit: isFormSubmitDisable(_formData, formStateData.formError),
        }));
      }

      spinnerCtx.setLoadingIndicator(false);
    };

    spinnerCtx.setLoadingIndicator(true);
    initFormComponents();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage>
      <TabPanel
        tabs={[
          {
            icon: "",
            label: t("createAuthSignConfig.tabHeader"),
            children: (
              <>
                <div style={{ fontWeight: "bold", height: "30px" }}>
                  {selectedAuthSignConfigId ? t("createAuthSignConfig.editHeader") : t("createAuthSignConfig.createHeader")}
                </div>
                <AppDynamicForm formField={FormFieldContent} handleSubmit={handleOnSave} validated={formStateData.validated} />
                <FormButtonToolbar
                  onCancelClick={handleCancel}
                  onSubmitClick={handleOnSave}
                  isSubmitDisabled={formStateData.disableFormSubmit}
                />
              </>
            ),
          },
        ]}
        selectedTab={"0"}
      />
    </FormPage>
  );
};

export default CreateAuthSignConfigForm;
